import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { adminGet, adminPost } from "../services/api";
import {
  LightbulbRegular, WarningRegular, ErrorCircleRegular, CheckmarkCircleRegular, InfoRegular, ArrowClockwiseRegular, ArrowSyncRegular,
  FlashRegular, MoneyRegular, TranslateRegular, ServerRegular, BrainRegular, NewsRegular, ShieldErrorRegular, ArrowRightRegular,
  PulseRegular, SparkleRegular,
} from "@fluentui/react-icons";
import { fluentIcon } from "../lib/fluentIcon";

const Lightbulb = fluentIcon(LightbulbRegular);
const AlertTriangle = fluentIcon(WarningRegular);
const AlertCircle = fluentIcon(ErrorCircleRegular);
const CheckCircle = fluentIcon(CheckmarkCircleRegular);
const Info = fluentIcon(InfoRegular);
const RefreshCw = fluentIcon(ArrowClockwiseRegular);
const Loader2 = fluentIcon(ArrowSyncRegular);
const Zap = fluentIcon(FlashRegular);
const DollarSign = fluentIcon(MoneyRegular);
const Languages = fluentIcon(TranslateRegular);
const Server = fluentIcon(ServerRegular);
const Brain = fluentIcon(BrainRegular);
const Newspaper = fluentIcon(NewsRegular);
const ShieldAlert = fluentIcon(ShieldErrorRegular);
const ArrowRight = fluentIcon(ArrowRightRegular);
const Activity = fluentIcon(PulseRegular);
const Sparkles = fluentIcon(SparkleRegular);

interface Recommendation {
  type: string;
  severity: string;
  title: string;
  description: string;
  action?: string;
}

const SEVERITY_CONFIG: Record<string, { icon: any; bg: string; border: string; text: string; badge: string }> = {
  critical: { icon: AlertCircle, bg: "bg-red-950/30", border: "border-red-900/40", text: "text-red-400", badge: "bg-red-500/20 text-red-400 border-red-500/30" },
  warning: { icon: AlertTriangle, bg: "bg-yellow-950/20", border: "border-yellow-900/30", text: "text-yellow-400", badge: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" },
  info: { icon: Info, bg: "bg-brand-blue/5", border: "border-brand-blue/20", text: "text-brand-blue", badge: "bg-brand-blue/20 text-brand-blue border-brand-blue/30" },
  success: { icon: CheckCircle, bg: "bg-emerald-950/20", border: "border-emerald-900/30", text: "text-emerald-400", badge: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" },
};

const TYPE_ICONS: Record<string, any> = {
  ai: Brain, cost: DollarSign, ingestion: Newspaper, translation: Languages,
  performance: Zap, errors: ShieldAlert, quality: Lightbulb, success: CheckCircle,
  architecture: Server,
};

function RecCard({ rec }: { rec: Recommendation }) {
  const sev = SEVERITY_CONFIG[rec.severity] ?? SEVERITY_CONFIG.info;
  const TypeIcon = TYPE_ICONS[rec.type] ?? Lightbulb;
  const SevIcon = sev.icon;
  return (
    <div className={`${sev.bg} border ${sev.border} rounded-xl p-5`}>
      <div className="flex items-start gap-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${sev.bg} border ${sev.border}`}>
          <TypeIcon size={18} className={sev.text} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border uppercase tracking-wider ${sev.badge}`}>{rec.severity}</span>
            <span className="text-[10px] text-slate-500 uppercase tracking-wider">{rec.type}</span>
          </div>
          <h3 className="text-sm font-semibold text-slate-200 mb-1">{rec.title}</h3>
          <p className="text-xs text-slate-400 leading-relaxed">{rec.description}</p>
          {rec.action && (
            <div className="flex items-center gap-1.5 mt-3 text-xs text-brand-blue">
              <ArrowRight size={10} />
              <span className="font-medium">{rec.action}</span>
            </div>
          )}
        </div>
        <SevIcon size={16} className={`${sev.text} flex-shrink-0 mt-1`} />
      </div>
    </div>
  );
}

export default function AdvisorPage() {
  const { token } = useAuth();
  interface AIInsight {
    grade: string;
    summary: string;
    actions: { title: string; saving: string; detail: string }[];
    risks: string[];
  }
  const [aiAnalysis, setAiAnalysis] = useState<AIInsight | null>(null);
  const [aiRaw, setAiRaw] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiHidden, setAiHidden] = useState(false);

  const { data, isLoading, refetch, dataUpdatedAt } = useQuery<{ recommendations: Recommendation[] }>({
    queryKey: ["recommendations"],
    queryFn: () => adminGet<{ recommendations: Recommendation[] }>("/api/v1/admin/recommendations", token),
    refetchInterval: 5 * 60_000,
  });

  const recs = data?.recommendations ?? [];
  const archRecs = recs.filter(r => r.type === "architecture" || r.type === "cost");
  const opsRecs = recs.filter(r => r.type !== "architecture" && r.type !== "cost");
  const criticals = recs.filter(r => r.severity === "critical");
  const warnings = recs.filter(r => r.severity === "warning");
  const infos = recs.filter(r => r.severity === "info");
  const successes = recs.filter(r => r.severity === "success");

  const runAIAnalysis = async () => {
    setAiLoading(true);
    setAiAnalysis(null);
    setAiRaw(null);
    setAiHidden(false);
    try {
      const metricsContext = recs.map(r => `[${r.severity.toUpperCase()}] ${r.title}: ${r.description}`).join("\n");
      const userContent = `System diagnostics (operational checks use 24h data, architecture/cost decisions use 7-day trends):\n\n${metricsContext}\n\nReturn JSON analysis.`;
      const d = await adminPost<{ content: string }>("/api/v1/admin/ai-analyze", { kind: "advisor", context: userContent }, token);
      const content = d.content ?? "";
      try {
        setAiAnalysis(JSON.parse(content));
      } catch {
        setAiRaw(content || "No response");
      }
    } catch (e: any) {
      setAiRaw(`Error: ${e.message}`);
    }
    setAiLoading(false);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Optimization Advisor</h1>
          <p className="text-sm text-slate-500 mt-1">Real-time infrastructure analysis with AI-powered recommendations.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={runAIAnalysis} disabled={aiLoading || isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 rounded-lg text-sm font-medium transition-colors">
            {aiLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            AI Analysis
          </button>
          <button onClick={() => refetch()} disabled={isLoading}
            className="flex items-center gap-2 px-3 py-2 bg-slate-900 border border-slate-800 hover:bg-slate-800 rounded-lg text-sm transition-colors">
            {isLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          </button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        <SummaryPill label="Critical" count={criticals.length} color={criticals.length > 0 ? "bg-red-500/10 border-red-500/20 text-red-400" : "bg-slate-800/50 border-slate-700 text-slate-500"} />
        <SummaryPill label="Warnings" count={warnings.length} color={warnings.length > 0 ? "bg-yellow-500/10 border-yellow-500/20 text-yellow-400" : "bg-slate-800/50 border-slate-700 text-slate-500"} />
        <SummaryPill label="Suggestions" count={infos.length} color={infos.length > 0 ? "bg-brand-blue/10 border-brand-blue/20 text-brand-blue" : "bg-slate-800/50 border-slate-700 text-slate-500"} />
        <SummaryPill label="All Good" count={successes.length} color={successes.length > 0 ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400" : "bg-slate-800/50 border-slate-700 text-slate-500"} />
      </div>

      {/* Show button when hidden */}
      {aiHidden && (aiAnalysis || aiRaw) && (
        <button onClick={() => setAiHidden(false)}
          className="flex items-center gap-2 mb-4 px-3 py-2 bg-purple-950/20 border border-purple-900/30 rounded-lg text-xs text-purple-400 hover:bg-purple-900/20 transition-colors w-full justify-center">
          <Sparkles size={12} /> Show AI Analysis
        </button>
      )}

      {/* AI Analysis Panel */}
      {!aiHidden && (aiAnalysis || aiRaw || aiLoading) && (
        <div className="bg-purple-950/20 border border-purple-900/30 rounded-xl p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-purple-400" />
              <h2 className="text-sm font-semibold text-purple-300">Gemini AI Analysis</h2>
              <span className="text-[10px] text-slate-500 bg-slate-800/50 px-2 py-0.5 rounded-full">Operational: 24h | Architecture: 7 days</span>
            </div>
            <button onClick={() => setAiHidden(true)} className="text-slate-500 hover:text-slate-300 text-xs px-2 py-1 border border-slate-700 rounded-md hover:border-slate-500">Hide</button>
          </div>
          {aiLoading ? (
            <div className="flex items-center gap-2 text-slate-500 py-4"><Loader2 size={14} className="animate-spin" /> Analyzing infrastructure with Gemini...</div>
          ) : aiAnalysis ? (
            <div>
              {/* Grade + Summary */}
              <div className="flex items-start gap-4 mb-5">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-2xl font-black flex-shrink-0 ${
                  aiAnalysis.grade === "A" ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" :
                  aiAnalysis.grade === "B" ? "bg-brand-blue/20 text-brand-blue border border-brand-blue/30" :
                  aiAnalysis.grade === "C" ? "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30" :
                  "bg-red-500/20 text-red-400 border border-red-500/30"
                }`}>
                  {aiAnalysis.grade}
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Infrastructure Health Grade</div>
                  <p className="text-sm text-slate-300 leading-relaxed">{aiAnalysis.summary}</p>
                </div>
              </div>

              {/* Actions */}
              {aiAnalysis.actions && aiAnalysis.actions.length > 0 && (
                <div className="mb-4">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Recommended Actions</div>
                  <div className="space-y-2">
                    {aiAnalysis.actions.map((a, i) => (
                      <div key={i} className="bg-slate-800/40 rounded-lg p-3 flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-brand-blue/20 text-brand-blue text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-slate-200">{a.title}</span>
                            <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">{a.saving}</span>
                          </div>
                          <p className="text-[10px] text-slate-400 mt-1">{a.detail}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Risks */}
              {aiAnalysis.risks && aiAnalysis.risks.length > 0 && (
                <div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Risks & Concerns</div>
                  <div className="space-y-1.5">
                    {aiAnalysis.risks.map((r, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs text-yellow-400">
                        <AlertTriangle size={10} className="mt-0.5 flex-shrink-0" />
                        <span className="text-slate-400">{r}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : aiRaw ? (
            <div className="text-xs text-slate-300 leading-relaxed whitespace-pre-wrap">{aiRaw}</div>
          ) : null}
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center gap-3 py-16 text-slate-500">
          <Loader2 size={20} className="animate-spin" />
          <span className="text-sm">Analyzing your infrastructure...</span>
        </div>
      )}

      {/* Architecture Decisions */}
      {!isLoading && archRecs.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Server size={16} className="text-purple-400" />
            <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">Architecture & Cost Decisions</h2>
          </div>
          <div className="space-y-3">
            {archRecs.map((rec, i) => <RecCard key={i} rec={rec} />)}
          </div>
        </div>
      )}

      {/* Operational Health */}
      {!isLoading && opsRecs.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Activity size={16} className="text-brand-blue" />
            <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">Operational Health</h2>
          </div>
          <div className="space-y-3">
            {opsRecs.map((rec, i) => <RecCard key={i + 100} rec={rec} />)}
          </div>
        </div>
      )}

      {dataUpdatedAt && (
        <div className="text-[10px] text-slate-600 mt-4 text-center">
          Last analyzed: {new Date(dataUpdatedAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}
        </div>
      )}
    </div>
  );
}

function SummaryPill({ label, count, color }: { label: string; count: number; color: string }) {
  return (
    <div className={`rounded-xl border p-3 text-center ${color}`}>
      <div className="text-xl font-bold">{count}</div>
      <div className="text-[10px] uppercase tracking-wider mt-0.5">{label}</div>
    </div>
  );
}
